'use client'

import { InformationDialog, PaymentType } from '@repo/shared/components'
import { useConvertNumber, useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useDebounce } from '@repo/ui/hooks'
import { useWithdrawOnlinePaymentSchema } from '@repo/shared/schemas'
import {
  useCalculateWithdrawalCost,
  useGetBanks,
  UserUserBankAccount,
  useUserPortfolio,
} from '@repo/services'
import { convertToCardNumber } from '@repo/tools'
import {
  Button,
  Card,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  InputNumber,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
  ActionDetailsCondition,
} from '@repo/ui/components'
import { bankDetailsList } from '@repo/ui/components/bank-card-field/constants'
// import { useIsDesktop } from '@repo/ui/hooks'
import { WithdrawOnlinePaymentAddCard } from './withdraw-online-payment.add-card'
// import { WithdrawOnlinePaymentCTA } from './withdraw-online-payment.cta'
import { WithdrawOnlinePaymentDialog } from './withdraw-online-payment.dialog'
import { useEffect, useMemo } from 'react'

export type Card = {
  enName: string | undefined
  card: string | undefined
  sheba: string | undefined
  default: boolean
  id: number
}

export const WithdrawOnlinePaymentContainer = () => {
  const { t } = useTranslate()
  const { convertNumberToLetter } = useConvertNumber()
  const { data, isLoading } = useGetBanks({})
  const { data: { data: { accounts = [] } = {} } = {} } = useUserPortfolio()
  const account = useMemo(() => {
    return accounts?.find((account) => account.type === 'fiat')
  }, [accounts])

  const cards: Card[] = useMemo(
    () =>
      (data?.data?.bankAccounts || [])
        .filter((bank) => bank.details?.iban && bank.details?.iban !== '')
        .map((bank: UserUserBankAccount) => ({
          id: bank.id ?? 0,
          enName: bank.details?.bankName ?? 'Unknown',
          card:
            bank.type === 'card'
              ? convertToCardNumber(Number(bank.data))
              : undefined,
          sheba: bank.details?.iban,
          default: bank.default ?? false,
        })),
    [data?.data?.bankAccounts],
  )
  const defaultCard: Card | undefined = useMemo(
    () => cards.find((c) => c.default),
    [cards],
  )
  const { schema } = useWithdrawOnlinePaymentSchema(
    (account?.availableBalance ?? 0) / 10,
  )
  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      card: defaultCard ? (defaultCard.sheba ?? '') : undefined,
      amount: undefined,
    },
  })
  useEffect(() => {
    form.setValue('card', defaultCard ? defaultCard.sheba : undefined)
  }, [defaultCard, form])

  const amount = +form.watch('amount')?.replace(/,/g, '') * 10
  const debouncedAmount = useDebounce(amount, 500)
  const { data: { data: calculateWithdrawalCost } = {} } =
    useCalculateWithdrawalCost({
      amount: debouncedAmount,
      currency: 'IRR',
    })

  const onSubmit = (data: FormSchema<typeof schema>) => {
    console.log(data)
  }

  // const isDesktop = useIsDesktop()

  const getBankIcon = (enName: string) => {
    const logo = bankDetailsList.find(
      (bank) =>
        bank.enNames.some(
          (name) => name.toLowerCase() === enName.toLowerCase(),
        ) || bank.name.toLowerCase() === enName.toLowerCase(),
    )
    return logo?.icon ?? <Icon name='CardLine' size='sm' />
  }

  const selectedCard = useMemo(() => {
    return cards.find(
      (c) => c.sheba?.toLowerCase() === form.watch('card')?.toLowerCase(),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, form.watch('card')])

  const handleSelectBalance = () => {
    form.setValue(
      'amount',
      ((account?.availableBalance ?? 0) / 10).toLocaleString(),
      {
        shouldValidate: true,
      },
    )
  }

  return (
    <Card className='max-w-container-md mx-auto p-xl' withoutBorder>
      {/* {isDesktop && <WithdrawOnlinePaymentCTA origin='container' />} */}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          id='withdraw-online-payment-form'
        >
          <Col className='gap-md'>
            <FormField
              control={form.control}
              name='card'
              extra={<WithdrawOnlinePaymentAddCard />}
              render={({ field }) => (
                <Select {...field} onValueChange={field.onChange}>
                  <FormItemWrapper
                    title={t('withdrawOnlinePaymentPage_select_card')}
                    inputSize='md'
                  >
                    <SelectTrigger inputSize='md' className='w-full'>
                      <SelectValue
                        placeholder={t(
                          'withdrawOnlinePaymentPage_select_card_placeholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormItemWrapper>
                  <SelectContent>
                    {cards.map((c, idx) => (
                      <SelectItem key={idx} value={c.sheba || String(idx)}>
                        <Row className='gap-xs items-center'>
                          {c.enName && getBankIcon(c.enName)}
                          <Typography size='md'>{c.sheba}</Typography>
                        </Row>
                      </SelectItem>
                    ))}
                    {cards.length === 0 && !isLoading && (
                      <Col className='min-h-20 text-center m-auto gap-md'>
                        <Typography
                          size='md'
                          className='m-auto'
                          color='text-neutral-surface-80'
                        >
                          {t('depositOnlinePaymentPage_select_card_empty')}
                        </Typography>
                      </Col>
                    )}
                  </SelectContent>
                </Select>
              )}
            />
            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItemWrapper
                  title={t('withdrawOnlinePaymentPage_amount_desired_toman')}
                  inputSize='md'
                  caption={t('withdrawOnlinePaymentPage_amount_in_words', {
                    number: convertNumberToLetter(
                      field.value?.replace(/,/g, '') ?? 0,
                    ),
                  })}
                >
                  <InputNumber
                    {...field}
                    thousandSeparator={true}
                    placeholder={t('withdrawOnlinePaymentPage_enter_amount')}
                    inputSize='md'
                    className='w-full'
                  />
                </FormItemWrapper>
              )}
            />
            <Row className='justify-between mt-md mb-xs'>
              <Typography
                size='xs'
                weight='regular'
                color='text-neutral-surface-80'
              >
                {t('withdrawOnlinePaymentPage_balance')}
              </Typography>
              <Row className='w-fit gap-xs'>
                <PaymentType
                  value={account?.availableBalance}
                  numberSize='md'
                  size='xs'
                  isBold={false}
                />
                <Button
                  size='xs'
                  width='full'
                  color='base'
                  variant='ghost'
                  className='w-fit px-sm py-2xs'
                  type='button'
                  onClick={handleSelectBalance}
                >
                  {t('withdrawOnlinePaymentPage_select_balance')}
                </Button>
              </Row>
            </Row>

            <ActionDetailsCondition>
              <Row className='justify-between mt-md mb-xs'>
                <Row className='gap-2xs'>
                  <Typography
                    size='xs'
                    weight='regular'
                    color='text-neutral-surface-80'
                  >
                    {t('withdrawOnlinePaymentPage_transaction_fee')}
                  </Typography>
                  <InformationDialog
                    title={t('withdrawOnlinePaymentPage_transaction_fee')}
                  >
                    {t('info_account_to_account_fee_description')}
                  </InformationDialog>
                </Row>

                <PaymentType
                  size='2xs'
                  numberColor='text-neutral-surface-80'
                  isBold={false}
                  value={calculateWithdrawalCost?.charge}
                />
              </Row>
              <Row className='justify-between mb-lg'>
                <Typography
                  size='xs'
                  weight='regular'
                  color='text-neutral-surface-80'
                >
                  {t('withdrawOnlinePaymentPage_final_withdraw')}
                </Typography>

                <PaymentType
                  size='2xs'
                  numberColor='text-neutral-surface-80'
                  isBold={false}
                  value={
                    amount ? amount - (calculateWithdrawalCost?.charge || 0) : 0
                  }
                />
              </Row>
              <WithdrawOnlinePaymentDialog
                card={selectedCard}
                amount={amount}
                fee={+(calculateWithdrawalCost?.charge || 0)}
                isValidate={form.formState.isValid}
              >
                <Button
                  size='lg'
                  color='base'
                  className='w-full mt-lg'
                  type='submit'
                >
                  {t('withdrawOnlinePaymentPage_withdraw')}
                </Button>
              </WithdrawOnlinePaymentDialog>
            </ActionDetailsCondition>
          </Col>
        </form>
      </Form>
    </Card>
  )
}
